import { z } from "zod";
import { CurrencySchema, DecimalSchema } from "./branded";

/**
 * Shared, route-agnostic building blocks: ids, timestamps, JSON values, money,
 * pagination, and the response envelopes every route group reuses.
 */

// ── JSON ────────────────────────────────────────────────────────
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/** Arbitrary JSON value — used for opaque columns (indicators, traces, data). */
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

/** A JSON object (record). */
export const JsonObjectSchema = z.record(z.string(), JsonValueSchema);
export type JsonObject = z.infer<typeof JsonObjectSchema>;

// ── scalars ─────────────────────────────────────────────────────
/** Opaque entity id (TEXT primary key). */
export const IdSchema = z.string().min(1);
export type Id = z.infer<typeof IdSchema>;

/** UTC epoch milliseconds (all timestamps in the system, §2). */
export const EpochMsSchema = z.number().int();
export type EpochMs = z.infer<typeof EpochMsSchema>;

/**
 * Money value: a decimal string paired with its currency. Never a float, never
 * a bare number — arithmetic lives in `@richrich/core/money`.
 */
export const MoneySchema = z.object({
  amount: DecimalSchema,
  currency: CurrencySchema,
});
export type Money = z.infer<typeof MoneySchema>;

// ── request helpers ─────────────────────────────────────────────
/** `:id` path param. */
export const IdParamSchema = z.object({ id: IdSchema });
export type IdParam = z.infer<typeof IdParamSchema>;

/** Cursor pagination query (values arrive as strings → coerce `limit`). */
export const CursorQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type CursorQuery = z.infer<typeof CursorQuerySchema>;

// ── response envelopes ──────────────────────────────────────────
/** Generic success acknowledgement for effectful routes with no body. */
export const OkResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponseSchema>;

/** Stable machine-readable error codes surfaced by the API error middleware. */
export const ErrorCodeSchema = z.enum([
  "UNCLAIMED",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "VALIDATION",
  "CONFLICT",
  "FEATURE_LOCKED",
  "RATE_LIMITED",
  "HIGH_VALUE_UNCONFIRMED",
  "TRADING_DISARMED",
  "UPSTREAM",
  "INTERNAL",
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** Uniform error body. */
export const ErrorResponseSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    requestId: z.string().optional(),
    details: JsonValueSchema.optional(),
  }),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/**
 * Cursor-paginated list wrapper. `nextCursor` is null when the last page is
 * reached.
 */
export const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
